import {
  SupplierConnectionState,
  SupplierDataRequestState,
  SupplierInboxNextAction,
} from './supplierInboxContracts';
import { inclusiveEndDateFromExclusive } from './supplierFactorInterval';
// Module-scope helpers (not components/hooks) — callers inside a component
// pass their bound `t` (useTranslation('freeSupplier')) for a live-reactive
// label; the i18n singleton default keeps every non-React caller (this
// file's own test suite included) working unchanged, same pattern as
// helpers/validationMessages.js's resolveValidationMessage.
import i18n from 'i18n';

// The request-LINE seed/CN-good-resolution helpers used to live here; they
// moved to supplierRequestEntrySeed.js (shared by RequestDirectEntryPage AND
// DataRequestsPage's inline "provide data" flow) — re-exported so existing
// importers of this module keep working unchanged.
export {
  RequestLineCnGoodResolution,
  defaultDirectEntryLineIndex,
  requestLineToDirectEntrySeed,
  resolveRequestLineCnGood,
  resolveRequestLineEntrySeed,
} from './supplierRequestEntrySeed';

export const STATE_LABELS = Object.freeze({
  pending: 'Pending',
  accepted: 'Accepted',
  in_progress: 'In progress',
  partially_fulfilled: 'Partially fulfilled',
  fulfilled: 'Fulfilled',
  declined: 'Declined',
  expired: 'Expired',
  cancelled: 'Cancelled',
  terminated: 'Terminated',
  unfulfilled: 'Unfulfilled',
});

export const getRequestPrimaryCta = (nextAction, t = i18n.t.bind(i18n)) => {
  switch (nextAction) {
    case SupplierInboxNextAction.ReviewConnection:
      return null;
    case SupplierInboxNextAction.ReviewRequest:
      return {
        action: 'accept_request',
        label: t('freeSupplier:inbox.cta.acceptRequest', 'Accept request'),
      };
    case SupplierInboxNextAction.FulfilRequest:
      return {
        action: 'provide_data',
        label: t('freeSupplier:inbox.cta.provideData', 'Provide data'),
      };
    case SupplierInboxNextAction.ViewRequest:
      return { action: 'view_request', label: t('freeSupplier:inbox.cta.view', 'View') };
    default:
      return null;
  }
};

// Connection-level CTA is only "Accept connection". The combined "Accept & start
// request" is owned per-request by getRequestCombinedAcceptCta (single source of truth).
export const getConnectionPrimaryCtas = (connection, t = i18n.t.bind(i18n)) => {
  if (connection.nextAction !== SupplierInboxNextAction.ReviewConnection) return [];
  return [
    {
      action: 'accept_connection',
      label: t('freeSupplier:inbox.cta.acceptConnection', 'Accept connection'),
    },
  ];
};

export const getRequestCombinedAcceptCta = (connection, request, t = i18n.t.bind(i18n)) => {
  if (
    connection.nextAction !== SupplierInboxNextAction.ReviewConnection ||
    connection.state !== SupplierConnectionState.Pending ||
    request.state !== SupplierDataRequestState.Pending
  ) {
    return null;
  }
  return {
    action: 'accept_connection_and_request',
    label: t('freeSupplier:inbox.cta.acceptAndStartRequest', 'Accept & start request'),
    requestId: request.id,
  };
};

export const canDeclineConnection = (state) => state === SupplierConnectionState.Pending;
export const canTerminateConnection = (state) => state === SupplierConnectionState.Accepted;
export const canDeclineRequest = (state) => state === SupplierDataRequestState.Pending;
export const canCancelRequest = (state) =>
  [
    SupplierDataRequestState.Accepted,
    SupplierDataRequestState.InProgress,
    SupplierDataRequestState.PartiallyFulfilled,
  ].includes(state);

// Builder (not a static map) so every state label routes through i18n — the
// STATE_LABELS keys/English values above stay the fallback/reference; only
// the rendered value is translated.
const buildStateLabels = (t) => ({
  pending: t('freeSupplier:inbox.state.pending', 'Pending'),
  accepted: t('freeSupplier:inbox.state.accepted', 'Accepted'),
  in_progress: t('freeSupplier:inbox.state.inProgress', 'In progress'),
  partially_fulfilled: t('freeSupplier:inbox.state.partiallyFulfilled', 'Partially fulfilled'),
  fulfilled: t('freeSupplier:inbox.state.fulfilled', 'Fulfilled'),
  declined: t('freeSupplier:inbox.state.declined', 'Declined'),
  expired: t('freeSupplier:inbox.state.expired', 'Expired'),
  cancelled: t('freeSupplier:inbox.state.cancelled', 'Cancelled'),
  terminated: t('freeSupplier:inbox.state.terminated', 'Terminated'),
  unfulfilled: t('freeSupplier:inbox.state.unfulfilled', 'Unfulfilled'),
});

export const formatState = (state, t = i18n.t.bind(i18n)) => buildStateLabels(t)[state] || state;

// Locate a data request (and its owning connection) inside the inbox response by
// request id. Used by the request-tailored surface (/supplier/requests/:id).
export const findSupplierInboxRequest = (connections, requestId) => {
  for (const connection of connections || []) {
    for (const request of connection.requests || []) {
      if (request.id === requestId) return { connection, request };
    }
  }
  return null;
};

// The distinct CN codes a request asks for, in first-seen order.
export const requestCnCodes = (request) => [
  ...new Set((request?.lines || []).map((line) => line.cnCode)),
];

// A request is fully covered once every line is fulfilled by published data.
export const isRequestFullyCovered = (request) =>
  (request?.lines || []).length > 0 &&
  (request.lines || []).every((line) => line.fulfilment?.state === 'fulfilled');

// A3/item-11: a request line's installation SCOPE (a raw installation _id
// string, or unset/unscoped) → an explicit tri-state resolution, instead of
// conflating "unscoped" with "matched":
//   - `matched` — a scoped line whose id resolves by ID (never by name,
//     mirroring supplierDataRequestFulfilment.ts scopeMatches:
//     `line.installation === revision.installationId.toString()`), OR an
//     unscoped line where exactly one installation exists (the only case an
//     unscoped line can be auto-selected without guessing).
//   - `selection_required` — an unscoped line with SEVERAL installations to
//     choose from; auto-picking "the first one" would silently guess.
//   - `unresolvable_scoped` — a scoped line naming an installation id that
//     isn't among the supplier's own docs (renamed/deleted/stale id). Item 4
//     (REVERSED ruling): a substitute installation can never fulfil a scoped
//     request (the backend matches by the exact named id), so this state is
//     NOT recoverable by picking a different installation — only by picking a
//     different (resolvable) request line, or contacting the declarant.
export const RequestLineInstallationResolution = Object.freeze({
  Matched: 'matched',
  SelectionRequired: 'selection_required',
  UnresolvableScoped: 'unresolvable_scoped',
});

export const resolveRequestLineInstallationState = (line, installationDocs) => {
  const docs = installationDocs || [];
  const requestedId = line?.installation;
  if (requestedId) {
    const match = docs.find((d) => String(d._id) === String(requestedId)) || null;
    return match
      ? { state: RequestLineInstallationResolution.Matched, installation: match }
      : { state: RequestLineInstallationResolution.UnresolvableScoped, installation: null };
  }
  if (docs.length === 1) {
    return { state: RequestLineInstallationResolution.Matched, installation: docs[0] };
  }
  return { state: RequestLineInstallationResolution.SelectionRequired, installation: null };
};

// Convenience accessor for callers that only need the resolved doc (or null).
export const resolveRequestLineInstallation = (line, installationDocs) =>
  resolveRequestLineInstallationState(line, installationDocs).installation;

// R1 (S7 fix batch 3): reduce an entire request's lines down to the SINGLE
// installation scope a bulk template upload can safely target. Unlike direct
// entry — which seeds one line at a time and can resolve per-line — a
// template upload POSTs one installationId for the whole file
// (`RequestUploadPage` has no line picker), so every scoped line has to
// agree on the same target before we can preselect+lock it. Reuses the exact
// per-line tri-state for the common cases (an unscoped request, or every
// scoped line naming the same installation); a request whose lines scope to
// SEVERAL different installations can't be reduced to one upload target, so
// it resolves as `unresolvable_scoped` too — the upload flow must block
// rather than silently guess which line's installation to honor.
export const resolveRequestInstallationState = (request, installationDocs) => {
  const lines = request?.lines || [];
  const scopedIds = [...new Set(lines.map((line) => line?.installation).filter(Boolean))];
  if (scopedIds.length > 1) {
    return { state: RequestLineInstallationResolution.UnresolvableScoped, installation: null };
  }
  const representativeLine =
    scopedIds.length === 1 ? { installation: scopedIds[0] } : lines[0] || null;
  return resolveRequestLineInstallationState(representativeLine, installationDocs);
};

// Item 7: half-open [startInclusive, endExclusive) — display the INCLUSIVE
// end date (endExclusive - 1 day) like every other interval display in this
// module (formatDraftInterval, requestLineToDirectEntrySeed), instead of the
// raw exclusive boundary (which reads one day past the request's real last
// day, e.g. a Jan–Mar request showing "1 Apr" instead of "31 Mar").
export const formatRequestInterval = (line) => {
  const date = (value) =>
    new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(value));
  const inclusiveEnd = inclusiveEndDateFromExclusive(line.interval.endExclusive);
  return `${date(line.interval.startInclusive)} – ${date(inclusiveEnd || line.interval.endExclusive)}`;
};
